import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import CallToAction from '../components/CallToAction';

const { FiHeart, FiEye, FiShield, FiClock, FiUsers, FiStar } = FiIcons;

const BOOK_COVER = "https://media-manager-c.questera.ai/greta-media/ae05bbc051fb17176d2c348bf77438a7a494626431a725f22ebe70ac4579d949c16512ea83019a888cf26e0e50680ce7/images/aW1hZ2UvanBlZw==/3614b5604f8ec9fc0b13dbae720eb360.jpg";

const signs = [
  { icon: FiEye, num: "01", title: "He Watches Over You Quietly", desc: "He notices when you're tired before you say a word. His gaze carries a tenderness that never announces itself — it simply stays." },
  { icon: FiShield, num: "02", title: "He Fixes Things You Never Asked Him To", desc: "The drawer that sticks. The frame slightly crooked for months. He handles it without fanfare, because your comfort is his quiet project." },
  { icon: FiClock, num: "03", title: "He Gives You His Time Without Complaint", desc: "In a world that rewards busyness, he chooses to be present. Unhurried. Undistracted. Fully there." },
  { icon: FiHeart, num: "04", title: "He Remembers What You Thought He Forgot", desc: "Your coffee order. The name of your difficult colleague. The thing you mentioned once. He stores these like treasures." },
  { icon: FiUsers, num: "05", title: "He Stays Calm When You Cannot", desc: "When the world gets loud, he becomes still. His steadiness is not indifference — it is a shelter he built just for you." },
  { icon: FiStar, num: "06", title: "He Handles the Things You Dread", desc: "The difficult call. The errand you hate. The problem you've been avoiding. He steps in because your ease matters to him." },
  { icon: FiShield, num: "07", title: "He Makes You Feel Safe Without Saying Why", desc: "Around him, you exhale. Your guard comes down. That feeling of safety is not accidental — it is constructed, day by day, by him." },
  { icon: FiHeart, num: "08", title: "He Simply Stays", desc: "When things are hard, when you are not easy to love, when the world offers him easier paths — he stays. And in staying, he says everything." },
];

const AboutBookPage = () => (
  <main className="pt-24">
    {/* Hero Banner */}
    <section className="py-20 bg-stone-900 text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-brand-800/40 via-stone-900 to-stone-900"></div>
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
            <span className="text-brand-400 font-semibold tracking-widest uppercase text-sm mb-4 block">About the Book</span>
            <h1 className="text-4xl md:text-5xl font-serif font-bold leading-tight mb-6">
              The Silent Language of Love
            </h1>
            <p className="text-xl text-stone-300 font-serif italic mb-6">
              8 Signs He's More Romantic Than You Know
            </p>
            <p className="text-stone-400 text-lg leading-relaxed mb-8">
              A relationship book that argues romance is often expressed through quiet consistency, service, presence, and protection — not grand gestures. It reframes ordinary acts as deeply loving behaviors.
            </p>
            <a href="https://www.amazon.com/dp/B0GHP512XW" target="_blank" rel="noopener noreferrer"
              className="inline-block px-8 py-4 bg-white text-stone-900 rounded-full font-semibold hover:bg-brand-100 transition-colors">
              Get Your Copy on Amazon
            </a>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
            className="flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-brand-400/20 blur-3xl rounded-full scale-110"></div>
              <img src={BOOK_COVER} alt="The Silent Language of Love Book Cover"
                className="w-64 md:w-80 rounded-xl shadow-2xl relative z-10 object-cover aspect-[2/3]" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>

    {/* Main Idea */}
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
          <span className="text-brand-600 font-semibold tracking-widest uppercase text-sm mb-4 block">The Core Message</span>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-6">What This Book Is Really About</h2>
        </motion.div>
        <div className="grid md:grid-cols-2 gap-8">
          {[
            { label: "The Problem", text: "Love should not be measured only by flowers, surprises, public declarations, or expensive gestures. Most readers feel their relationship is loving but not 'romantic enough' — this book addresses exactly that." },
            { label: "The Solution", text: "The most meaningful romance is often invisible: showing up every day, easing burdens, solving problems, and creating emotional safety. This is mature, lasting intimacy." },
            { label: "What Changes", text: "You will leave this book seeing reliability not as boring, but as one of the most deeply romantic qualities a partner can possess." },
            { label: "Who It's For", text: "Especially for readers who feel their relationship is loving but not 'romantic enough' — this book validates quiet, stable partnership and helps you see the love already there." },
          ].map((item, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="p-6 bg-stone-50 rounded-2xl border border-stone-100">
              <p className="text-xs font-bold uppercase tracking-widest text-brand-500 mb-2">{item.label}</p>
              <p className="text-stone-700 leading-relaxed">{item.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* 8 Signs */}
    <section className="py-20 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <span className="text-brand-600 font-semibold tracking-widest uppercase text-sm mb-4 block">The 8 Signs</span>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900">The Signs Explored in the Book</h2>
        </motion.div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {signs.map((sign, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }}
              className="p-6 bg-white rounded-2xl border border-stone-100 hover:shadow-lg transition-shadow duration-300 group">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-3xl font-serif font-bold text-stone-200 group-hover:text-brand-200 transition-colors">{sign.num}</span>
                <div className="w-10 h-10 bg-brand-50 rounded-xl flex items-center justify-center group-hover:bg-brand-100 transition-colors">
                  <SafeIcon icon={sign.icon} className="w-5 h-5 text-brand-600" />
                </div>
              </div>
              <h3 className="font-serif font-bold text-stone-900 mb-2 text-base">{sign.title}</h3>
              <p className="text-stone-500 text-sm leading-relaxed">{sign.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Style & Tone */}
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-6">Writing Style & Tone</h2>
          <p className="text-lg text-stone-600 leading-relaxed mb-8">
            Written in a highly emotional, persuasive, and poetic style. It uses long reflections, repeated phrases, and vivid examples to make a single point from different angles: what looks ordinary on the surface may actually be love in its strongest form. The tone is intimate and reassuring — it tries to change how readers define romance.
          </p>
          <div className="p-8 bg-stone-900 rounded-3xl text-white">
            <p className="text-xl md:text-2xl font-serif italic leading-relaxed">
              "This book reveals the hidden romance in everyday acts of care, loyalty, and presence."
            </p>
          </div>
        </motion.div>
      </div>
    </section>

    <div id="get-copy"><CallToAction /></div>
  </main>
);

export default AboutBookPage;